import React, { useState, useEffect, useCallback } from 'react';
import AmplifierSearchScreen from '../AmplifierSearchScreen/AmplifierSearchScreen';

// ─────────────────────────────────────────────────────────────────────────────
// AmpOfTheDayAdmin — admin tab for Feature #141
//
// Props:
//   api       — pre-configured axios instance (from SiteAdminScreen)
//   readOnly  — boolean
// ─────────────────────────────────────────────────────────────────────────────

const DAYS_AHEAD = 14;

// Return YYYY-MM-DD (local time) for a date offset from today
function dateOffset(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Format a date string (YYYY-MM-DD) as a human-readable label
function fmtDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric'
  });
}

const AmpOfTheDayAdmin = ({ api, readOnly }) => {
  const [subTab, setSubTab] = useState('today');
  const [today, setToday] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Amp picker modal — schedule
  const [addingDate, setAddingDate] = useState(null); // the date slot being filled
  const [removeLoading, setRemoveLoading] = useState(null);

  // Feature Now
  const [featuringNow, setFeaturingNow] = useState(false);
  const [featureNowLoading, setFeatureNowLoading] = useState(false);
  const [, setFeaturedTopic] = useState(null);
  const [featuredToday, setFeaturedToday] = useState([]);

  // AOTD history — for picker badges and the History tab
  const [aotdHistory, setAotdHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState('');

  // Instagram
  const [instagramPosting, setInstagramPosting] = useState(false);

  const showError = (msg) => { setError(msg); setSuccessMsg(null); };
  const showSuccess = (msg) => { setSuccessMsg(msg); setError(null); };

  const handlePostToInstagram = async () => {
    setInstagramPosting(true);
    try {
      const res = await api.post('/aotd/post-instagram');
      const { instagram_post_id, facebook_post_id } = res.data;
      const platforms = [instagram_post_id && 'Instagram', facebook_post_id && 'Facebook'].filter(Boolean);
      showSuccess(`Posted to ${platforms.join(' & ')}!`);
      loadToday();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to post to social media');
    } finally {
      setInstagramPosting(false);
    }
  };

  // ── Load today's activated amp ─────────────────────────────────────────────

  const loadToday = useCallback(async () => {
    setTodayLoading(true);
    setError(null);
    try {
      const res = await api.get('/public/amp-of-the-day');
      setToday(res.data.amp !== undefined ? res.data : res.data);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load today\'s amp');
    } finally {
      setTodayLoading(false);
    }
  }, [api]);

  const loadFeaturedToday = useCallback(async () => {
    try {
      const res = await api.get('/aotd/featured-today');
      setFeaturedToday(res.data);
    } catch {
      // non-critical, silently ignore
    }
  }, [api]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/aotd/history');
      setAotdHistory(res.data);
    } catch {
      // non-critical — picker still works without history badges
    }
  }, [api]);

  // ── Load schedule ──────────────────────────────────────────────────────────

  const loadSchedule = useCallback(async () => {
    setScheduleLoading(true);
    setError(null);
    try {
      const res = await api.get(`/aotd/schedule?days=${DAYS_AHEAD}`);
      setSchedule(res.data);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadToday();
    loadSchedule();
    loadFeaturedToday();
    loadHistory();
  }, [loadToday, loadSchedule, loadFeaturedToday, loadHistory]);

  // ── Manual activate ────────────────────────────────────────────────────────

  const handleActivateToday = async () => {
    setActivating(true);
    setError(null);
    try {
      await api.post('/aotd/activate-today');
      showSuccess('Today\'s Amp of the Day activated!');
      await loadToday();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to activate');
    } finally {
      setActivating(false);
    }
  };

  // ── Amp search ─────────────────────────────────────────────────────────────

  const handleAddToSchedule = async (amplifier_id, date) => {
    setError(null);
    try {
      await api.post('/aotd/schedule', { amplifier_id, scheduled_date: date });
      showSuccess(`Scheduled for ${fmtDate(date)}`);
      setAddingDate(null);
      await loadSchedule();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to schedule amp');
    }
  };

  const handleFeatureNow = async (amplifier_id) => {
    setFeatureNowLoading(true);
    setFeaturingNow(false);
    setError(null);
    try {
      const res = await api.post('/aotd/feature-now', { amplifier_id });
      setFeaturedTopic(res.data);
      showSuccess(`Discussion thread created: "${res.data.title}"`);
      await loadFeaturedToday();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to feature amp');
    } finally {
      setFeatureNowLoading(false);
    }
  };

  const handleRemove = async (aotd_id) => {
    setRemoveLoading(aotd_id);
    setError(null);
    try {
      await api.delete(`/aotd/schedule/${aotd_id}`);
      showSuccess('Schedule entry removed');
      await loadSchedule();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to remove entry');
    } finally {
      setRemoveLoading(null);
    }
  };

  // ── Build the 14-day date list ─────────────────────────────────────────────

  const dateSlots = Array.from({ length: DAYS_AHEAD }, (_, i) => {
    const d = dateOffset(i);
    const scheduled = schedule.find(s => {
      return typeof s.scheduled_date === 'string'
        ? s.scheduled_date.slice(0, 10) === d
        : new Date(s.scheduled_date).toISOString().slice(0, 10) === d;
    });
    return { date: d, scheduled };
  });

  // ── Styles ─────────────────────────────────────────────────────────────────

  const cardStyle = {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  };

  const btnPrimary = {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
  };

  const btnDanger = {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '0.3rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  };

  const btnGhost = {
    background: 'none',
    border: '1px solid #007bff',
    color: '#007bff',
    borderRadius: '4px',
    padding: '0.3rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  };

  return (
    <div>
      <h3 style={{ marginBottom: '0.5rem' }}>Amp of the Day</h3>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Schedule and manage the daily amp spotlight. A new amp is activated automatically at midnight UTC.
      </p>

      {/* Sub-tab nav */}
      <div style={{ display: 'flex', gap: '1.5rem', borderBottom: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
        {['today', 'schedule', 'history'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.75rem 0',
              borderBottom: subTab === t ? '2px solid #007bff' : '2px solid transparent',
              color: subTab === t ? '#007bff' : '#666',
              fontWeight: subTab === t ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '0.95rem',
            }}
          >
            {t === 'today' ? "Today's Amp" : t === 'schedule' ? '14-Day Queue' : 'History'}
          </button>
        ))}
      </div>

      {/* Feedback messages */}
      {error && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {successMsg && (
        <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {successMsg}
        </div>
      )}

      {/* Today tab */}
      {subTab === 'today' && (
        <div>
          {todayLoading ? (
            <p style={{ color: '#666' }}>Loading...</p>
          ) : today && today.amplifier_id ? (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem' }}>
                    {today.manufacturer_name} {today.model || today.amplifier_name}
                    {today.year_introduced && ` (${today.year_introduced})`}
                  </h4>
                  <p style={{ margin: '0 0 0.75rem', color: '#666', fontSize: '0.9rem' }}>
                    Amp of the Day — {fmtDate(today.scheduled_date?.slice(0, 10) || today.scheduled_date)}
                    {today.is_random && <span style={{ marginLeft: '0.5rem', backgroundColor: '#fff3cd', color: '#856404', padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.8rem' }}>random</span>}
                  </p>
                  {today.topic_id && (
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                      <a href={`/discussions/topic/${today.topic_id}`} target="_blank" rel="noreferrer">
                        View discussion thread
                      </a>
                      {today.message_count > 0 && ` · ${today.message_count} repl${today.message_count === 1 ? 'y' : 'ies'}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={cardStyle}>
              <p style={{ margin: '0 0 1rem', color: '#666' }}>
                No amp has been activated for today yet.
              </p>
              {!readOnly && (
                <button
                  style={btnPrimary}
                  onClick={handleActivateToday}
                  disabled={activating}
                >
                  {activating ? 'Activating…' : 'Activate Today'}
                </button>
              )}
            </div>
          )}

          {today && today.amplifier_id && today.topic_id && !readOnly && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                Today's amp is already active. The discussion thread has been created.
              </p>
              {(() => {
                const igOk = today.instagram_post_id && today.instagram_post_id !== 'failed' && today.instagram_post_id !== 'pending';
                const fbOk = today.facebook_post_id && today.facebook_post_id !== 'failed' && today.facebook_post_id !== 'pending';
                const igFailed = today.instagram_post_id === 'failed';
                const fbFailed = today.facebook_post_id === 'failed';
                if (igOk && fbOk) {
                  return <p style={{ color: '#28a745', fontSize: '0.85rem', margin: 0 }}>✓ Posted to Instagram &amp; Facebook</p>;
                }
                if (igOk && !fbOk) {
                  return <p style={{ color: '#28a745', fontSize: '0.85rem', margin: 0 }}>✓ Posted to Instagram {fbFailed && <span style={{ color: '#dc3545' }}> · Facebook failed</span>}</p>;
                }
                return (
                  <>
                    {(igFailed || fbFailed) && (
                      <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                        ⚠ {igFailed && 'Instagram'}{igFailed && fbFailed && ' & '}{fbFailed && 'Facebook'} posting failed — check Error Log for details
                      </p>
                    )}
                    <button style={btnGhost} onClick={handlePostToInstagram} disabled={instagramPosting}>
                      {instagramPosting ? 'Posting…' : igFailed ? '🔄 Retry Instagram Post' : '📸 Post to Instagram & Facebook'}
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {today && today.amplifier_id && !today.topic_id && !readOnly && (
            <button
              style={btnPrimary}
              onClick={handleActivateToday}
              disabled={activating}
            >
              {activating ? 'Activating…' : 'Activate Today'}
            </button>
          )}

          {!readOnly && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
              <h5 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>Feature an Amp Now</h5>
              <p style={{ margin: '0 0 0.75rem', color: '#666', fontSize: '0.85rem' }}>
                Immediately open a discussion thread for any amp — independent of today's scheduled amp.
              </p>
              <button
                style={btnGhost}
                onClick={() => setFeaturingNow(true)}
                disabled={featureNowLoading}
              >
                {featureNowLoading ? 'Creating…' : '+ Feature an Amp Now'}
              </button>

              {featuredToday.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600, color: '#444' }}>Featured today:</p>
                  {featuredToday.map(f => (
                    <div key={f.topic_id} style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                      <strong>{f.manufacturer_name} {f.model || f.amplifier_name}</strong>
                      {' — '}
                      <span style={{ color: '#888' }}>
                        {new Date(f.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {' · '}
                      <a href={`/discussions/topic/${f.topic_id}`} target="_blank" rel="noreferrer">View thread</a>
                      {f.message_count > 0 && ` · ${f.message_count} repl${f.message_count === 1 ? 'y' : 'ies'}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Schedule queue tab */}
      {subTab === 'schedule' && (
        <div>
          {scheduleLoading ? (
            <p style={{ color: '#666' }}>Loading schedule...</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>Amp</th>
                  <th style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>Status</th>
                  {!readOnly && <th style={{ padding: '0.5rem 0.75rem' }} />}
                </tr>
              </thead>
              <tbody>
                {dateSlots.map(({ date, scheduled }) => (
                  <React.Fragment key={date}>
                    <tr style={{ borderBottom: '1px solid #dee2e6', backgroundColor: date === dateOffset(0) ? '#fff9e6' : 'transparent' }}>
                      <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap', fontWeight: date === dateOffset(0) ? 'bold' : 'normal' }}>
                        {fmtDate(date)}
                        {date === dateOffset(0) && <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#856404' }}>today</span>}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        {scheduled ? (
                          <span>
                            {scheduled.manufacturer_name} {scheduled.model || scheduled.amplifier_name}
                            {scheduled.year_introduced && ` (${scheduled.year_introduced})`}
                            {scheduled.topic_id && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#28a745' }}>● active</span>}
                          </span>
                        ) : (
                          <span style={{ color: '#aaa', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        {scheduled?.topic_id ? (
                          <span style={{ fontSize: '0.8rem', color: '#28a745' }}>Activated</span>
                        ) : scheduled ? (
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>Scheduled</span>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Empty</span>
                        )}
                      </td>
                      {!readOnly && (
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {scheduled && !scheduled.topic_id ? (
                            <button
                              style={btnDanger}
                              disabled={removeLoading === scheduled.aotd_id}
                              onClick={() => handleRemove(scheduled.aotd_id)}
                            >
                              {removeLoading === scheduled.aotd_id ? '…' : 'Remove'}
                            </button>
                          ) : !scheduled ? (
                            <button
                              style={btnGhost}
                              onClick={() => setAddingDate(date)}
                            >
                              + Add Amp
                            </button>
                          ) : null}
                        </td>
                      )}
                    </tr>

                                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* History tab */}
      {subTab === 'history' && (() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const past = aotdHistory.filter(h => h.scheduled_date.slice(0, 10) < todayStr);
        const q = historySearch.trim().toLowerCase();
        const filtered = q
          ? past.filter(h =>
              h.manufacturer_name.toLowerCase().includes(q) ||
              h.model.toLowerCase().includes(q) ||
              h.scheduled_date.slice(0, 10).includes(q)
            )
          : past;

        return (
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Search by manufacturer, model, or date (YYYY-MM-DD)…"
                value={historySearch}
                onChange={e => setHistorySearch(e.target.value)}
                style={{
                  width: '100%', maxWidth: '420px', padding: '0.45rem 0.75rem',
                  border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem',
                }}
              />
              <span style={{ marginLeft: '0.75rem', color: '#888', fontSize: '0.85rem' }}>
                {filtered.length} of {past.length} past entries
              </span>
            </div>

            {past.length === 0 ? (
              <p style={{ color: '#888' }}>No past entries yet.</p>
            ) : filtered.length === 0 ? (
              <p style={{ color: '#888' }}>No entries match your search.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem', color: '#495057' }}>Date</th>
                    <th style={{ padding: '0.5rem 0.75rem', color: '#495057' }}>Manufacturer</th>
                    <th style={{ padding: '0.5rem 0.75rem', color: '#495057' }}>Model</th>
                    <th style={{ padding: '0.5rem 0.75rem', color: '#495057' }}>Amp Page</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((h, i) => (
                    <tr
                      key={`${h.amplifier_id}-${h.scheduled_date}`}
                      style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#f8f9fa' }}
                    >
                      <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap', color: '#495057' }}>
                        {fmtDate(h.scheduled_date.slice(0, 10))}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{h.manufacturer_name}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{h.model}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <a
                          href={`/amplifier-components/${h.amplifier_id}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#007bff' }}
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      {/* Feature Now amp picker modal */}
      {featuringNow && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Feature an amp now"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '2rem 1rem',
            overflowY: 'auto',
          }}
          onClick={e => { if (e.target === e.currentTarget) setFeaturingNow(false); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '1100px',
            padding: '1.5rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, fontSize: '1.1rem' }}>Select an amp to feature now</h4>
              <button
                onClick={() => setFeaturingNow(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666', lineHeight: 1 }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <AmplifierSearchScreen
              onSelectAmp={amp => handleFeatureNow(amp.amplifier_id)}
              aotdHistory={aotdHistory}
            />
          </div>
        </div>
      )}

      {/* Schedule amp picker modal */}
      {addingDate && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Select an amplifier"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '2rem 1rem',
            overflowY: 'auto',
          }}
          onClick={e => { if (e.target === e.currentTarget) setAddingDate(null); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '1100px',
            padding: '1.5rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, fontSize: '1.1rem' }}>
                Select an amp for <strong>{fmtDate(addingDate)}</strong>
              </h4>
              <button
                onClick={() => setAddingDate(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666', lineHeight: 1 }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <AmplifierSearchScreen
              onSelectAmp={amp => handleAddToSchedule(amp.amplifier_id, addingDate)}
              aotdHistory={aotdHistory}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AmpOfTheDayAdmin;
